import { invalidateCurrentUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { type JoinedTeamsResponse } from '@heidi/workspace/contracts';
import { GET_JOINED_TEAMS, useQueryJoinedTeams } from '@heidi/workspace/data';
import { useEffect, useRef } from 'react';

function normalizeTeamIds(joinedTeams: JoinedTeamsResponse | undefined): string[] {
  if (!joinedTeams?.joined_teams?.length) {
    return [];
  }
  return joinedTeams.joined_teams.map((team) => team.team_id);
}

/**
 * Side-effect-only component to keep billing in sync with team membership.
 *
 * This addresses cases where membership changes server-side (e.g. Internal Admin direct add)
 * but billing remains cached as an individual tier (e.g. PRO) until a hard refresh/logout.
 */
export function TeamMembershipSync() {
  const { data: joinedTeams, isFetched } = useQueryJoinedTeams();
  const previousTeamIdsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const refetchJoinedTeamsOnFocus = () => {
      if (document.visibilityState !== 'visible') return;
      void reactQueryClient.invalidateQueries({ queryKey: [GET_JOINED_TEAMS] });
    };

    window.addEventListener('focus', refetchJoinedTeamsOnFocus);
    document.addEventListener('visibilitychange', refetchJoinedTeamsOnFocus);

    return () => {
      window.removeEventListener('focus', refetchJoinedTeamsOnFocus);
      document.removeEventListener('visibilitychange', refetchJoinedTeamsOnFocus);
    };
  }, []);

  useEffect(() => {
    if (!isFetched) return;

    const teamIds = normalizeTeamIds(joinedTeams);
    const key = teamIds.toSorted().join(',');

    if (previousTeamIdsKeyRef.current === null) {
      previousTeamIdsKeyRef.current = key;
      return;
    }

    if (previousTeamIdsKeyRef.current !== key) {
      previousTeamIdsKeyRef.current = key;
      void invalidateCurrentUserBillingInfo();
    }
  }, [isFetched, joinedTeams]);

  return null;
}
